/* import __COLOCATED_TEMPLATE__ from './submenu-nav.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { canSeeCustomAnswers } from 'embercom/helpers/can-see-custom-answers';
import RouteRegexes from 'embercom/lib/route-regexes';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';

interface MenuItem {
  title: string;
  icon?: string;
  url?: string;
  items?: MenuItem[];
  shouldHide?: boolean;
  stamp?: string;
  count?: number;
  external?: boolean;
  isSeparator?: boolean;
  status?: string;
}

const BATCH_TEST_ROUTE = 'apps.app.automation.fin-ai-agent.testing.index';
const SIMULATIONS_ROUTE = 'apps.app.automation.fin-ai-agent.simulations';

const MENU_STRUCTURE = (
  app: any,
  profileCount: number,
  customAnswersCount: number,
  contentSuggestionsCount: number,
  chatStatus: string,
  emailStatus: string,
  phoneStatus: string,
  canSeeCustomAnswers: boolean,
): MenuItem[] => [
  {
    title: 'ai-agent.new-submenu.analyze',
    icon: 'column-chart',
    items: [
      {
        title: 'ai-agent.new-submenu.discover',
        url: 'apps.app.automation.fin-ai-agent.analyze.discover',
        shouldHide: !app.canSeeFinReadiness,
      },
      {
        title: 'ai-agent.new-submenu.performance',
        url: 'apps.app.automation.fin-ai-agent.analyze.performance',
      },
      {
        title: 'ai-agent.new-submenu.ai-insights',
        url: 'apps.app.automation.fin-ai-agent.analyze.ai-insights',
        shouldHide: app.canSeeAiInsightsBeta,
      },
      {
        title: 'ai-agent.new-submenu.optimize-fin',
        url: app.canLinkToOptimizeReasons
          ? 'apps.app.automation.fin-ai-agent.analyze.optimize-fin-reasons'
          : 'apps.app.automation.fin-ai-agent.analyze.optimize-fin',
        shouldHide: !app.canSeeAiInsightsBeta,
      },
      {
        title: 'ai-agent.new-submenu.topics-explorer',
        url: 'apps.app.automation.fin-ai-agent.analyze.topics-explorer',
        shouldHide: !app.canSeeAiInsightsBeta,
      },
      {
        title: 'ai-agent.new-submenu.topic-trends',
        url: 'apps.app.automation.fin-ai-agent.analyze.topic-trends',
        shouldHide: !app.canUseTrendingTopics,
        stamp: 'ai-agent.beta-stamp',
      },
      {
        title: 'ai-agent.new-submenu.task-suggestions',
        url: 'apps.app.automation.fin-ai-agent.analyze.task-suggestions',
        shouldHide: !app.canUseTaskSuggestions,
        stamp: 'ai-agent.beta-stamp',
      },
      {
        title: 'ai-agent.new-submenu.conversations',
        external: true,
        url: 'inbox.workspace.inbox.fin-view-redirect',
      },
      {
        title: 'ai-agent.new-submenu.unresolved-questions',
        url: 'apps.app.automation.fin-ai-agent.analyze.unresolved-questions',
        shouldHide: app.canSeeAiInsightsBeta,
      },
    ],
  },
  {
    title: 'ai-agent.new-submenu.train',
    icon: 'article',
    items: [
      {
        title: 'ai-agent.new-submenu.content',
        url: 'apps.app.automation.fin-ai-agent.content',
      },
      {
        title: 'ai-agent.new-submenu.guidance',
        url: 'apps.app.automation.fin-ai-agent.guidance',
        shouldHide: !app.canUseFinGuidance,
      },
      {
        title: 'ai-agent.new-submenu.ai-categories',
        url: 'apps.app.automation.fin-ai-agent.categories',
        shouldHide: !app.canUseFinFirstAiCategories,
      },
      {
        title: 'ai-agent.new-submenu.escalation',
        url: 'apps.app.automation.fin-ai-agent.escalation',
        shouldHide: !app.canUseFeature('fin-escalation-rules'),
      },
      {
        title: 'operator.sub-menu.fin-ai-agent.tasks',
        url: 'apps.app.automation.fin-ai-agent.tasks.index',
      },
      {
        title: 'ai-agent.new-submenu.procedures',
        url: 'apps.app.automation.fin-ai-agent.procedures',
        shouldHide: !app.canUseFinProcedures,
      },
      {
        title: 'ai-agent.new-submenu.custom-answers',
        url: 'apps.app.automation.fin-ai-agent.custom-answers.index',
        count: customAnswersCount,
        shouldHide: !canSeeCustomAnswers,
      },
      {
        title: 'ai-agent.new-submenu.content-suggestions',
        url: 'apps.app.automation.fin-ai-agent.content-suggestions',
        count: contentSuggestionsCount,
      },
    ],
  },
  {
    title: 'ai-agent.new-submenu.test',
    icon: 'test',
    url: app.canUseSophisticatedTestingCentralisedPage ? undefined : BATCH_TEST_ROUTE,
    items: app.canUseSophisticatedTestingCentralisedPage
      ? [
          {
            title: 'ai-agent.new-submenu.batch-test',
            url: BATCH_TEST_ROUTE,
          },
          {
            title: 'ai-agent.new-submenu.simulations',
            url: SIMULATIONS_ROUTE,
          },
        ]
      : undefined,
    shouldHide: !app.canUseFinPlayground,
  },
  {
    title: 'ai-agent.new-submenu.deploy',
    icon: 'rocket-ship',
    items: [
      {
        title: 'ai-agent.new-submenu.chat',
        url: 'apps.app.automation.fin-ai-agent.deploy.chat',
        status: chatStatus,
      },
      {
        title: 'ai-agent.new-submenu.email',
        url: 'apps.app.automation.fin-ai-agent.deploy.email',
        status: emailStatus,
      },
      {
        title: 'ai-agent.new-submenu.phone',
        url: 'apps.app.automation.fin-ai-agent.deploy.phone',
        status: phoneStatus,
      },
      {
        title: 'ai-agent.new-submenu.profiles',
        url: 'apps.app.automation.fin-ai-agent.profiles.index',
        count: profileCount,
        shouldHide: profileCount === 0,
      },
    ],
  },
  {
    title: 'ai-agent.new-submenu.changelog',
    url: 'apps.app.automation.fin-ai-agent.changelog',
    icon: 'merge',
    shouldHide: !app.canUseFinChangelog,
  },
  {
    title: 'ai-agent.new-submenu.settings',
    url: 'apps.app.automation.fin-ai-agent.settings.index',
    icon: 'settings',
    shouldHide: app.canUseMultiBrandGA,
  },
  {
    title: 'ai-agent.new-submenu.settings',
    icon: 'settings',
    items: [
      {
        title: 'ai-agent.sub-menu.settings.general',
        url: 'apps.app.automation.fin-ai-agent.settings.index',
      },
      {
        title: 'ai-agent.sub-menu.settings.audiences',
        url: 'apps.app.automation.fin-ai-agent.settings.audiences',
      },
    ],
    shouldHide: !app.canUseMultiBrandGA,
  },
  {
    title: '',
    isSeparator: true,
  },
  {
    title: 'ai-agent.new-submenu.workflows',
    icon: 'workflows',
    url: 'apps.app.automation.workflows-overview',
  },
  {
    title: 'ai-agent.new-submenu.simple-automations',
    url: 'apps.app.automation.basics',
    icon: 'trigger',
  },
];

interface Signature {
  profileCount: number;
  customAnswersCount: number;
  contentSuggestionsCount: number;
}

export default class SubmenuNav extends Component<Signature> {
  @service appService: any;
  @service router: any;
  @service declare aiAgentSetupService: AiAgentSetupService;
  // The active section is the one where the page we currently are resides
  @tracked activeSection: any =
    this.menuItems.find((item: any) => this.checkIfSectionIsCurrentRoute(item))?.title || '';
  // we want to be able to open 2 sections, the active one and another one only
  @tracked secondaryOpenSection: any = '';
  @tracked isLoading = true;
  @service declare customerService: $TSFixMe;
  @service declare greatGuidanceService: GreatGuidanceService;

  checkIfSectionIsCurrentRoute(section: any) {
    return (
      this.router.currentRouteName === section.url ||
      section.items?.any(
        (item: any) =>
          this.router.currentURL
            .split('?')[0]
            .contains?.(this.router.urlFor(item.url, this.appService.app.id)) ||
          this.router.currentRouteName === item.url,
      )
    );
  }

  constructor(owner: any, args: any) {
    super(owner, args);
    this.router.on('routeDidChange', this.updateActiveSection);
    this.load();
  }

  async load() {
    this.isLoading = true;
    await this.aiAgentSetupService.load();
    this.isLoading = false;
  }

  get squaredRightRadius() {
    return (
      (RouteRegexes.automation.contentSuggestions.test(this.router.currentRouteName) &&
        this.appService.app.canUseAiInsightsSuggestionsUi) ||
      RouteRegexes.automation.optimizeReasons.test(this.router.currentRouteName)
    );
  }

  getActiveSection() {
    return this.menuItems.find((item: any) => this.checkIfSectionIsCurrentRoute(item))?.title || '';
  }

  willDestroy() {
    super.willDestroy();
    this.router.off('routeDidChange', this.updateActiveSection);
  }

  @action
  updateActiveSection() {
    this.activeSection =
      this.menuItems.find((item: any) => this.checkIfSectionIsCurrentRoute(item))?.title || '';
  }

  get menuItems() {
    return MENU_STRUCTURE(
      this.appService.app,
      this.args.profileCount,
      this.args.customAnswersCount,
      this.args.contentSuggestionsCount,
      this.aiAgentSetupService.isFinSetupChatRulesetLive ||
        this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('chat')
        ? 'online'
        : 'offline',
      this.aiAgentSetupService.isFinSetupEmailRulesetLive ||
        this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('email')
        ? 'online'
        : 'offline',
      this.aiAgentSetupService.isFinSetupPhoneRulesetLive ||
        this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('phone')
        ? 'online'
        : 'offline',
      canSeeCustomAnswers(this.appService.app, this.customerService),
    );
  }

  @action
  toggleSection(section: any) {
    // If section has both URL and items (like Workflows with debugger enabled)
    if (section.url && section.items) {
      // Navigate to the URL
      this.router.transitionTo(section.url);

      // Also handle submenu toggling
      let currentRouteSection = this.getActiveSection();
      if (currentRouteSection === section.title) {
        this.activeSection = this.activeSection === section.title ? '' : section.title;
      } else {
        this.secondaryOpenSection =
          this.secondaryOpenSection === section.title ? '' : section.title;
      }
      return;
    }

    // If section only has URL (no submenu items)
    if (section.url) {
      this.router.transitionTo(section.url);
      return;
    }

    // If section has no URL (only submenu items)
    let activeSection = this.getActiveSection();
    if (activeSection === section.title) {
      this.activeSection = this.activeSection === section.title ? '' : section.title;
    } else {
      this.secondaryOpenSection = this.secondaryOpenSection === section.title ? '' : section.title;
    }
  }

  @action
  isSectionActive(section: any) {
    let currentRouteName = this.router.currentRouteName;

    if (
      section.url === BATCH_TEST_ROUTE &&
      !this.appService.app.canUseSophisticatedTestingCentralisedPage
    ) {
      // Match for any route that starts with the Batch Test route
      return currentRouteName.startsWith('apps.app.automation.fin-ai-agent.testing.');
    }

    // If section has both URL and items (like Workflows with debugger enabled),
    // don't make the main button active - let the submenu items be active instead
    if (section.url && section.items) {
      return false;
    }

    return currentRouteName === section.url;
  }

  @action
  isSectionOpen(section: any) {
    return this.activeSection === section.title || this.secondaryOpenSection === section.title;
  }

  @action
  isSubmenuItemActive(item: any) {
    let currentRouteName = this.router.currentRouteName;

    // Special handling for testing routes
    if (item.url === BATCH_TEST_ROUTE) {
      return currentRouteName.startsWith('apps.app.automation.fin-ai-agent.testing.');
    }

    if (item.url === SIMULATIONS_ROUTE) {
      return currentRouteName === SIMULATIONS_ROUTE;
    }

    return (
      currentRouteName === item.url ||
      this.router.currentURL
        .split('?')[0]
        .contains?.(this.router.urlFor(item.url, this.appService.app.id))
    );
  }

  get isWidgetVisible() {
    return !this.greatGuidanceService.hideFloatingWidget;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::SubmenuNav': typeof SubmenuNav;
    'ai-automation/submenu-nav': typeof SubmenuNav;
  }
}
